import { Pagination } from "@mui/material";
import {
  AUCTION_STATUS_NOT_STARTED,
  CHECKBOX_SIZE,
  DISABLED_SEARCH_ACTION_SEND_REQUESTS_ACCEPTED,
  DISABLED_SEARCH_ACTION_SEND_REQUESTS_ATTRIBUTED,
  DISABLED_SEARCH_ACTION_SEND_REQUESTS_CANCELLED,
  DISABLED_SEARCH_ACTION_SEND_REQUESTS_TRANSPORT_LIMIT,
  QUERY_PROGRESS_PENDING,
  SEARCH_ACTION_SEND_REQUEST,
  SEARCH_ACTION_START_AUTOMATIC_SEARCH,
  SEARCH_ACTION_START_MANUAL_SEARCH,
  SEARCH_TABLE_ACCEPTED,
  SEARCH_TABLE_CONTACTED,
  SEARCH_TABLE_DECLINED,
  SEARCH_TABLE_REJECTED,
  SEARCH_TABLE_ROW_COUNT_10,
  SEARCH_TABLE_VALIDATED,
  SEARCH_TYPE_HOSPITAL,
} from "core/consts";
import {
  declineDetailsReason,
  findSearchAction,
  shouldRedactHouseNumber,
} from "core/model/auctions";
import { formatDistance } from "core/model/utils/location";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntologiesForSelect } from "core/model/utils/ontologies/hooks";
import { grammaticalList } from "core/model/utils/strings";
import {
  AcceptedTableRequest,
  Auction,
  Careprovider,
  ContactedTableRequest,
  DeclinedTableRequest,
  DisabledSearchActionType,
  FilteredTableFilteredCandidate,
  GetOntologyType,
  PotentialReceiversTableCandidate,
  QueryProgress,
  RejectedTableRequest,
  SearchMergeTableRequest,
  SearchPanelRequest,
  SearchTable,
  SolutionFilterReasons,
  TableCount,
  TablePageState,
  TourElement,
  ValidatedTableRequest,
} from "core/types";
import { format, fromUnixTime } from "date-fns";
import { CheckboxInputFieldPresenter } from "ds_legacy/components/CheckboxInputField";
import { SelectInput } from "ds_legacy/components/SelectInput";
import {
  COLUMN_TYPE_DEFAULT_DATE,
  COLUMN_TYPE_DEFAULT_STRING,
} from "ds_legacy/components/Tables/GeneralTable/columnTypeConstants";
import { GeneralTableColumn } from "ds_legacy/components/Tables/GeneralTable/CustomCells";
import {
  COLUMN_TYPE_SEARCH_TABLE_CHECKBOX,
  COLUMN_TYPE_SEARCH_TABLE_CONTACT_ANYWAY,
  COLUMN_TYPE_SEARCH_TABLE_NAME_WITH_ICONS,
  COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT,
  COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT_POTENTIAL_RECEIVERS,
  COLUMN_TYPE_SEARCH_TABLE_STATUS,
} from "ds_legacy/components/Tables/GeneralTable/CustomCells/SearchTables";
import { HorizontalLayout } from "ds_legacy/materials/layouts";
import { dp, margin, sizing } from "ds_legacy/materials/metrics";
import {
  StopSearchWarning,
  getWarningMessage,
} from "dsl/ecosystems/PatientSearchMerge/Components/SearchActions/ActionModal/BackendCareproviderWarnings";
import { CareseekerNavigationHandlers } from "dsl/hooks";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import React, { CSSProperties } from "react";
import { Locale as LocaleString, useTranslations } from "translations";
import Translations from "translations/types";
import { usePotentialReceiverContext } from "./PotentialReceiverTable/PotentialReceiverContext";
import { RequestListState } from "./PotentialReceiverTable/PotentialReceiverTableReducer";

/** Narrow column for star/preference icon. Use % so table column widths can sum to 100%. */
const PREFERRED_WIDTH = "5%";

export function formatDistanceForSearchTables(
  distance: number | null | undefined,
  translations: Translations,
): string {
  if (!distance) return "";

  return translations.search.searchPageTables.distanceToPatient({
    distance: formatDistance(distance),
  });
}

export function columnPotentialStartDate<Data extends AcceptedTableRequest>({
  auction,
  locale,
  translations,
  width,
}: {
  auction: Auction;
  locale: Locale;
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_DEFAULT_STRING> {
  return {
    getProps: ({ potential_start_date, potential_start_time }) => {
      return {
        value: `${format(
          fromUnixTime(potential_start_date ?? auction.start_date),
          "PP",
          {
            locale,
          },
        )} ${potential_start_time || ""}`,
      };
    },
    title: translations.search.searchPageTables.columnNames.potentialStartDate,
    type: COLUMN_TYPE_DEFAULT_STRING,
    width: width ?? "10%",
  };
}

export function columnRejectDate<Data extends RejectedTableRequest>({
  translations,
  width,
}: {
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_DEFAULT_DATE> {
  return {
    getProps: ({ id, validation }) => {
      if (!validation) {
        console.error(
          `validation struct missing for columnRejectDate for auction request ${id}`,
        );
      }
      return {
        value: validation?.rejected_at,
        withTime: true,
      };
    },
    title: translations.search.searchPageTables.columnNames.rejectDate,
    type: COLUMN_TYPE_DEFAULT_DATE,
    width: width ?? "15%",
  };
}

export function columnDeclineReason<Data extends DeclinedTableRequest>({
  auction,
  getOntology,
  locale,
  translations,
  width,
}: {
  auction: Auction;
  getOntology: GetOntologyType;
  locale: Locale;
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_DEFAULT_STRING> {
  return {
    getProps: ({ response }) => ({
      value:
        declineDetailsReason({
          getOntology,
          isSearchMergeTable: true,
          locale,
          options: { __typename: "AuctionResponse", ...response },
          searchType: auction.search_type,
          translations,
          withComma: true,
        }) ?? undefined,
    }),
    title: translations.search.searchPageTables.columnNames.declineReason,
    type: COLUMN_TYPE_DEFAULT_STRING,
    width: width ?? "15%",
  };
}

export function columnDeclineDate<Data extends DeclinedTableRequest>({
  translations,
  width,
}: {
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_DEFAULT_DATE> {
  return {
    getProps: ({ id, response }) => {
      if (!response) {
        console.error(
          `received a request in declined table without a response, request ID: ${id}`,
        );
      }
      return {
        value: response?.declined_at,
        withTime: true,
      };
    },
    title: translations.search.searchPageTables.columnNames.declineDate,
    type: COLUMN_TYPE_DEFAULT_DATE,
    width: width ?? "10%",
  };
}

export const getFilterReasons = ({
  careprovider,
  getOntology,
  locale,
  localeString,
  solutionFilterReasons,
  translations,
}: {
  careprovider: Careprovider;
  getOntology: GetOntologyType;
  locale: Locale;
  localeString: LocaleString;
  solutionFilterReasons: SolutionFilterReasons;
  translations: Translations;
}) => {
  const warningMessages = Object.keys(solutionFilterReasons)
    .flatMap((filteredSolution) => {
      return solutionFilterReasons[filteredSolution].map((reason) => {
        return getWarningMessage({
          careprovider,
          getOntology,
          locale,
          localeString,
          reason,
          translations,
        });
      });
    })
    .truthy();

  const uniqueMessagesMap = new Map<string, StopSearchWarning>();
  warningMessages.forEach((messageObj) => {
    if (messageObj && !uniqueMessagesMap.has(messageObj.message)) {
      uniqueMessagesMap.set(messageObj.message, messageObj);
    }
  });

  const deduplicatedMessages = Array.from(uniqueMessagesMap.values());

  return {
    value: grammaticalList({
      array: deduplicatedMessages.map((messageObj) => messageObj.message),
      translations,
    }),
  };
};

export function columnFilterReason<
  Data extends FilteredTableFilteredCandidate,
>({
  getOntology,
  locale,
  localeString,
  translations,
  width,
}: {
  getOntology: GetOntologyType;
  locale: Locale;
  localeString: LocaleString;
  translations: Translations;
  width: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_DEFAULT_STRING> {
  return {
    getProps: ({ careprovider, solution_filter_reasons }) => {
      if (!careprovider || !solution_filter_reasons) return { value: "" };

      return getFilterReasons({
        careprovider,
        getOntology,
        locale,
        localeString,
        solutionFilterReasons: solution_filter_reasons,
        translations,
      });
    },
    title: translations.search.searchPageTables.columnNames.filterReason,
    type: COLUMN_TYPE_DEFAULT_STRING,
    width,
  };
}

export function columnStartDate<Data extends ValidatedTableRequest>({
  auction,
  locale,
  translations,
  width,
}: {
  auction: Auction;
  locale: Locale;
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_DEFAULT_STRING> {
  const dateFormat =
    auction.search_type === SEARCH_TYPE_HOSPITAL ? "PPp" : "PP";

  return {
    getProps: () => ({
      value: auction.start_date
        ? `${format(fromUnixTime(auction.start_date), dateFormat, {
            locale,
          })}`
        : "",
    }),
    title: translations.search.searchPageTables.columnNames.startDate,
    type: COLUMN_TYPE_DEFAULT_STRING,
    width: width ?? "10%",
  };
}

const getProviderName = ({
  auction,
  careprovider,
  getOntology,
  translations,
}: {
  auction: Auction;
  careprovider: SearchPanelRequest["careprovider"];
  getOntology: GetOntologyType;
  translations: Translations;
}) => {
  if (careprovider?.name) return careprovider?.name;

  if (auction.winner?.specialization) {
    const specialization = getOntology({
      key: auction.winner?.specialization,
      type: ONTOLOGIES.SPECIALIZATIONS,
    });
    return `${translations.search.searchPageTables.manuallyAssignedWinner.internalTransfer} - ${specialization}`;
  }
  if (!auction.winner?.unknown_provider) {
    console.warn(
      `FE received no careprovider name and winner not unknown for auction - ${auction.id}`,
    );
  }

  return translations.search.searchPageTables.manuallyAssignedWinner
    .unknownProvider;
};

// Shared column functions
export function columnProviderNameWithIcons<
  Data extends
    | AcceptedTableRequest
    | ContactedTableRequest
    | ValidatedTableRequest,
>({
  auction,
  getOntology,
  translations,
  width,
}: {
  auction: Auction;
  getOntology: GetOntologyType;
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_SEARCH_TABLE_NAME_WITH_ICONS> {
  return {
    getProps: ({ careprovider, patient_data_shared }) => ({
      auctionStatus: auction.status,
      name: getProviderName({
        auction,
        careprovider,
        getOntology,
        translations,
      }),
      patient_data_shared,
      shouldRedactHouseNumber: shouldRedactHouseNumber({
        search_type: auction.search_type,
        share_patient_data_automatically:
          auction.share_patient_data_automatically,
      }),
      testId: `provider-name-cell-${careprovider?.id}`,
    }),
    title: getOntology({
      key: auction.search_type,
      type: "receiverTypeSingular",
    }),
    type: COLUMN_TYPE_SEARCH_TABLE_NAME_WITH_ICONS,
    width: width ?? "20%",
  };
}

export function columnProviderName<
  Data extends
    | DeclinedTableRequest
    | FilteredTableFilteredCandidate
    | PotentialReceiversTableCandidate
    | RejectedTableRequest,
>({
  auction,
  getOntology,
  width,
}: {
  auction: Auction;
  getOntology: GetOntologyType;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_DEFAULT_STRING> {
  return {
    getProps: ({ careprovider }) => ({
      testId: `provider-name-cell-${careprovider?.id}`,
      value: careprovider?.name,
    }),
    title: getOntology({
      key: auction.search_type,
      type: "receiverTypeSingular",
    }),
    type: COLUMN_TYPE_DEFAULT_STRING,
    width,
  };
}

export function columnRequestDate<
  Data extends
    | ContactedTableRequest
    | DeclinedTableRequest
    | RejectedTableRequest,
>({
  translations,
  width,
}: {
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_DEFAULT_DATE> {
  return {
    getProps: ({ created_at }) => ({
      value: created_at,
      withTime: true,
    }),
    title: translations.search.searchPageTables.columnNames.dateOfRequest,
    type: COLUMN_TYPE_DEFAULT_DATE,
    width: width ?? "10%",
  };
}

export function columnDistance<
  Data extends
    | AcceptedTableRequest
    | ContactedTableRequest
    | FilteredTableFilteredCandidate
    | PotentialReceiversTableCandidate
    | ValidatedTableRequest,
>({
  isHidden,
  translations,
  width,
}: {
  isHidden: boolean;
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_DEFAULT_STRING> {
  return {
    getProps: ({ distance }) => ({
      value: distance
        ? formatDistanceForSearchTables(distance, translations)
        : translations.search.searchPageTables.manuallyAssignedWinner
            .dataNotAvailable,
    }),
    isHidden,
    title: translations.search.searchPageTables.columnNames.distance,
    type: COLUMN_TYPE_DEFAULT_STRING,
    width: width ?? "10%",
  };
}

export function columnStatus<
  Data extends
    | AcceptedTableRequest
    | ContactedTableRequest
    | DeclinedTableRequest
    | RejectedTableRequest
    | ValidatedTableRequest,
>({
  translations,
  width,
}: {
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_SEARCH_TABLE_STATUS> {
  return {
    getProps: ({ chip_status }) => ({ value: chip_status }),
    title: translations.search.searchPageTables.columnNames.status,
    tour: tourAttributes({
      stepKey: PRODUCT_TOURS.search_merge_started.steps.status.key,
      tourKey: PRODUCT_TOURS.search_merge_started.key,
    }),
    type: COLUMN_TYPE_SEARCH_TABLE_STATUS,
    width: width ?? "15%",
  };
}

export function columnLocation<
  Data extends
    | AcceptedTableRequest
    | ContactedTableRequest
    | FilteredTableFilteredCandidate
    | PotentialReceiversTableCandidate
    | DeclinedTableRequest
    | RejectedTableRequest
    | ValidatedTableRequest,
>({
  translations,
  width,
}: {
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_DEFAULT_STRING> {
  return {
    getProps: ({ careprovider }) => ({
      value:
        careprovider?.address?.zip_code && careprovider?.address?.city
          ? `${careprovider?.address?.zip_code} ${careprovider?.address?.city}`
          : translations.search.searchPageTables.manuallyAssignedWinner
              .dataNotAvailable,
    }),
    title: translations.search.searchPageTables.columnNames.location,
    type: COLUMN_TYPE_DEFAULT_STRING,
    width: width ?? "10%",
  };
}

export function columnPreferredInput<
  Data extends
    | ContactedTableRequest
    | DeclinedTableRequest
    | RejectedTableRequest
    | ValidatedTableRequest,
>({
  auction,
  invalidate,
  refresh,
  table,
  tour,
  translations,
  width,
}: {
  auction: Auction;
  invalidate?: () => void;
  refresh?: () => void;
  table: SearchTable;
  tour?: TourElement;
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT> {
  return {
    getProps: (auctionRequest) => ({
      auction,
      auctionRequest,
      invalidate,
      refresh,
      table,
    }),
    hint: translations.search.searchPageTables.columnNames.hint.patientWish,
    title: translations.search.patientWishInfoIcon,
    tour: tour,
    type: COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT,
    width: width ?? PREFERRED_WIDTH,
  };
}

export function columnPreferredInputPotentialReceivers<
  Data extends PotentialReceiversTableCandidate,
>({
  auction,
  table,
  translations,
  width,
}: {
  auction: Auction;
  table: SearchTable;
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<
  Data,
  typeof COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT_POTENTIAL_RECEIVERS
> {
  return {
    getProps: (auctionRequest) => ({
      auction,
      auctionRequest,
      table,
    }),
    hint: translations.search.searchPageTables.columnNames.hint.patientWish,
    title: translations.search.patientWishInfoIcon,
    tour: tourAttributes({
      stepKey: PRODUCT_TOURS.search_merge_not_started.steps.patient_wish.key,
      tourKey: PRODUCT_TOURS.search_merge_not_started.key,
    }),
    type: COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT_POTENTIAL_RECEIVERS,
    width: width ?? PREFERRED_WIDTH,
  };
}

export function columnSolution<Data extends SearchMergeTableRequest>({
  getOntology,
  translations,
  width,
}: {
  getOntology: GetOntologyType;
  translations: Translations;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_DEFAULT_STRING> {
  return {
    getProps: ({ solutions }) => ({
      value: grammaticalList({
        array: solutions?.clone()?.sort((a, b) => a - b),
        getOntology,
        ontologyType: ONTOLOGIES.SOLUTION,
        translations,
        useShortValues: true,
      }),
    }),
    title: translations.search.searchPageTables.columnNames.solutions,
    type: COLUMN_TYPE_DEFAULT_STRING,
    width,
  };
}

type TablePageStateSetter = (pageState: TablePageState) => void;

// Pagination component
export function TablePagination({
  currentTablePageState,
  setTablePageState,
  total,
}: {
  currentTablePageState: TablePageState;
  setTablePageState: TablePageStateSetter;
  total: number | undefined;
}) {
  const rowsPerPage = useGetOntologiesForSelect({
    sort: true,
    type: ONTOLOGIES.ROWS_PER_PAGE,
  });

  if (!total || total <= SEARCH_TABLE_ROW_COUNT_10) return null;

  const totalPages = Math.ceil(total / currentTablePageState.count);

  return (
    <HorizontalLayout
      aligned
      data-testid="table-pagination"
      justify="space-between"
      margin={margin(2.5, 0)}
    >
      <Pagination
        count={totalPages}
        page={currentTablePageState.page}
        onChange={(e, page) =>
          setTablePageState({ ...currentTablePageState, page })
        }
      />
      <SelectInput
        elementName="rowsPerPage"
        MenuProps={{ disablePortal: true }}
        options={rowsPerPage}
        size="small"
        style={{ width: dp(125) }}
        value={currentTablePageState.count}
        variant="outlined"
        onChange={(e) =>
          setTablePageState({
            ...currentTablePageState,
            count: e as TableCount,
            page: 1,
          })
        }
      />
    </HorizontalLayout>
  );
}

type ProviderCheckboxHeaderAriaTypes = {
  checked: boolean | "mixed";
  label: string;
};

const useGetCheckboxHeaderAriaType = ({
  automaticSearch,
  indeterminate,
}: {
  automaticSearch: boolean;
  indeterminate: boolean;
}): ProviderCheckboxHeaderAriaTypes => {
  const translations = useTranslations();

  if (indeterminate)
    return {
      checked: "mixed",
      label:
        translations.search.searchPageTables.accessibility
          .selectAllIntermediary,
    };

  if (automaticSearch)
    return {
      checked: true,
      label: translations.search.searchPageTables.accessibility.deselectAll,
    };

  return {
    checked: false,
    label: translations.search.searchPageTables.accessibility.selectAll,
  };
};

export function SelectProviderCheckboxHeader({
  isDisabled,
}: {
  isDisabled: boolean;
}) {
  const {
    deselectAllRequests,
    requestListState: { automaticSearch, requestFormState },
    setAutomaticSearch,
  } = usePotentialReceiverContext();

  const indeterminate = Object.values(requestFormState).some(
    (request) => request.manually_selected,
  );

  const ariaType = useGetCheckboxHeaderAriaType({
    automaticSearch,
    indeterminate,
  });

  return (
    <div style={{ textOverflow: "" }}>
      <CheckboxInputFieldPresenter
        checkboxSize={CHECKBOX_SIZE.LARGE}
        disabled={isDisabled}
        elementName="automatic-search"
        indeterminate={indeterminate}
        inputProps={{
          "aria-checked": ariaType.checked,
          "aria-label": ariaType.label,
        }}
        label=""
        value={automaticSearch}
        onChange={() => {
          setAutomaticSearch();
          deselectAllRequests();
        }}
      />
    </div>
  );
}
export const shouldDisableSendRequest = (
  disabledActionType?: DisabledSearchActionType,
) => {
  const disabledActionTypes: DisabledSearchActionType[] = [
    DISABLED_SEARCH_ACTION_SEND_REQUESTS_ATTRIBUTED,
    DISABLED_SEARCH_ACTION_SEND_REQUESTS_CANCELLED,
    DISABLED_SEARCH_ACTION_SEND_REQUESTS_ACCEPTED,
    DISABLED_SEARCH_ACTION_SEND_REQUESTS_TRANSPORT_LIMIT,
  ];

  return disabledActionType
    ? disabledActionTypes.includes(disabledActionType)
    : false;
};

export const getCheckboxSettings = ({
  auction,
  requestListState,
  sendRequestsQueryProgress,
}: {
  auction: Auction;
  requestListState?: RequestListState;
  sendRequestsQueryProgress?: QueryProgress;
}): {
  autoSelectHeader: boolean;
  disableHeader: boolean;
  disableRow: boolean;
} => {
  const result = {
    autoSelectHeader: false,
    disableHeader: false,
    disableRow: false,
  };

  const sendRequestAction = findSearchAction(
    auction,
    SEARCH_ACTION_SEND_REQUEST,
  );
  const manualSearchAction = findSearchAction(
    auction,
    SEARCH_ACTION_START_MANUAL_SEARCH,
  );
  const automaticSearchAction = findSearchAction(
    auction,
    SEARCH_ACTION_START_AUTOMATIC_SEARCH,
  );

  if (!sendRequestAction) {
    return { ...result, disableHeader: true, disableRow: true };
  }

  if (
    auction.status === AUCTION_STATUS_NOT_STARTED &&
    (!manualSearchAction || manualSearchAction.context?.disabled)
  ) {
    result.disableHeader = true;
    result.disableRow = true;
  }

  if (automaticSearchAction?.context?.disabled) {
    result.autoSelectHeader = false;
  } else if (automaticSearchAction) {
    result.autoSelectHeader = true;
  }

  if (
    shouldDisableSendRequest(sendRequestAction.context?.disabled ?? undefined)
  ) {
    result.autoSelectHeader = false;
    result.disableHeader = true;
    result.disableRow = true;
  }

  if (sendRequestsQueryProgress === QUERY_PROGRESS_PENDING) {
    result.disableHeader = true;
    result.disableRow = true;
  }

  if (requestListState?.automaticSearch) {
    result.disableRow = true;
  }

  return result;
};
export function columnCheckbox<Data extends PotentialReceiversTableCandidate>({
  auction,
  requestListState,
  sendRequestsQueryProgress,
  width,
}: {
  auction: Auction;
  requestListState?: RequestListState;
  sendRequestsQueryProgress: QueryProgress;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_SEARCH_TABLE_CHECKBOX> {
  const { disableHeader, disableRow } = getCheckboxSettings({
    auction,
    requestListState,
    sendRequestsQueryProgress,
  });

  return {
    getProps: ({ careprovider, id }) => {
      return {
        disabled: disableRow,
        value: {
          careprovider_id: careprovider.id,
          id,
          manually_selected: false,
          patient_preferred: false,
          patient_preferred_reason: undefined,
        },
      };
    },
    title: <SelectProviderCheckboxHeader isDisabled={disableHeader} />,
    type: COLUMN_TYPE_SEARCH_TABLE_CHECKBOX,
    width: width ?? sizing(2.5),
  };
}

export function handleSearchMergeRowClick<
  Data extends { id: number; recommendation_id?: number },
>({
  auctionId,
  navigationHandlers,
  patientId,
}: {
  auctionId: number;
  navigationHandlers: CareseekerNavigationHandlers;
  patientId: number;
}) {
  return ({
    event,
    rowData,
  }: {
    event: React.MouseEvent<HTMLElement>;
    rowData: Data;
  }) => {
    if (rowData.id <= 0) {
      console.error(
        `[DEV-14068] [handleSearchMergeRowClick] tried to access bad request id ${rowData.id}`,
      );
      return;
    }

    const isRecommendation = !!rowData.recommendation_id;

    if (event.ctrlKey || event.metaKey) {
      const recommendationParam = isRecommendation
        ? `?recommendation=true`
        : "";
      return window.open(
        `/app/clinic/dashboard/patient/${patientId}/auction/${auctionId}/request/${rowData.id}${recommendationParam}`,
        "_blank",
      );
    }

    return navigationHandlers.patient.goToRequest({
      auctionId,
      params: {
        recommendation: isRecommendation,
      },
      patientId,
      requestId: rowData.id,
    });
  };
}

const getSearchTableName = ({
  table,
  translations,
}: {
  table: SearchTable;
  translations: Translations;
}): string => {
  const tableTranslation =
    translations.ontologies.searchPageTableTitle.values?.[table];

  if (!tableTranslation) {
    console.error(
      `search table translation not configured in getSearchTableName for table ${table}`,
    );
    return "";
  }
  return tableTranslation;
};

export function getA11yRowClickProps<
  Request extends { careprovider?: { name: string } },
  Table extends
    | typeof SEARCH_TABLE_ACCEPTED
    | typeof SEARCH_TABLE_CONTACTED
    | typeof SEARCH_TABLE_DECLINED
    | typeof SEARCH_TABLE_REJECTED
    | typeof SEARCH_TABLE_VALIDATED,
>({ table, translations }: { table: Table; translations: Translations }) {
  return ({ data: request }: { data: Request }) => {
    return {
      "aria-label":
        translations.search.searchPageTables.accessibility.clickFunctionality({
          providerName: request?.careprovider?.name || "",
          tableName: getSearchTableName({ table, translations }),
        }),
      role: `link`,
    };
  };
}

export function getA11yPotentialTableRowClickProps({
  isSelected,
  translations,
}: {
  isSelected: (id: number) => boolean;
  translations: Translations;
}) {
  return ({ data: request }: { data: PotentialReceiversTableCandidate }) => {
    return {
      "aria-label": isSelected(request?.id)
        ? translations.search.searchPageTables.accessibility.deselectProvider({
            providerName: request?.careprovider?.name || "unbekannt",
          })
        : translations.search.searchPageTables.accessibility.selectProvider({
            providerName: request?.careprovider?.name || "unbekannt",
          }),
      role: `menuitemcheckbox`,
    };
  };
}

export function columnContactAnyway<
  Data extends FilteredTableFilteredCandidate,
>({
  auction,
  refresh,
  width,
}: {
  auction: Auction;
  refresh: () => void;
  width?: CSSProperties["width"];
}): GeneralTableColumn<Data, typeof COLUMN_TYPE_SEARCH_TABLE_CONTACT_ANYWAY> {
  return {
    getProps: ({
      careprovider,
      recommendation_id,
      send_fax,
      solution_filter_reasons,
    }) => {
      return {
        auction,
        careprovider,
        recommendationId: recommendation_id,
        refresh,
        sendFax: send_fax,
        solutionFilterReasons: solution_filter_reasons,
      };
    },
    type: COLUMN_TYPE_SEARCH_TABLE_CONTACT_ANYWAY,
    width: width ?? "5%",
  };
}

export const shouldDisableRowClick = ({
  auctionId,
  requestId,
  table,
}: {
  auctionId: number;
  requestId: number | null | undefined;
  table: SearchTable;
}) => {
  if (!requestId) {
    console.error(
      `[DEV-14068] [shouldDisableRowClick] no request id (${requestId}) received for row in search merge table ${table} for auction ${auctionId}`,
    );
    return true;
  }
  if (requestId > 0) {
    return false;
  }

  const isUnknownProviderValidation =
    table === SEARCH_TABLE_VALIDATED && requestId === -1;

  if (!isUnknownProviderValidation) {
    console.error(
      `[DEV-14068] [shouldDisableRowClick] bad request id (${requestId}) received for row in search merge table ${table} for auction ${auctionId}`,
    );
  }
  return true;
};
